import { Instrument, Security, SecurityCategory_CategoryCode, securityCategory_CategoryCodeToJSON } from '../proto/generated/security'

/** Mirrors backend SecurityCategoryMapping + isTradeableForNewCashMaster rules. */
export const TRADEABLE_CASH_INSTRUMENTS = new Set<string>([
    Instrument[Instrument.EQUITY],
    Instrument[Instrument.ETF],
    Instrument[Instrument.SGB],
    Instrument[Instrument.AIF],
    Instrument[Instrument.REIT],
    Instrument[Instrument.INVIT],
])

export const CM_CASH_CATEGORY_CODES = new Set<SecurityCategory_CategoryCode>([
    SecurityCategory_CategoryCode.STOCKS,
    SecurityCategory_CategoryCode.FUNDS,
    SecurityCategory_CategoryCode.BONDS,
])

export function isCmCashCategory(categoryCode?: SecurityCategory_CategoryCode): boolean {
    return categoryCode !== undefined && CM_CASH_CATEGORY_CODES.has(categoryCode)
}

/** True for equity issuers that own financials / shareholding / company pages. */
export function isCompanySecurity(
    security?: Pick<Security, 'instrument'> | null,
): boolean {
    return security?.instrument === Instrument[Instrument.EQUITY]
}

export function isTradeableCashInstrument(instrument?: string): boolean {
    return !!instrument && TRADEABLE_CASH_INSTRUMENTS.has(instrument)
}

/** Prefer API `tradeable` when present; fall back to instrument/category heuristics. */
export function isTradeableSecurity(
    security?: Pick<Security, 'tradeable' | 'instrument' | 'categoryCode'> | null,
): boolean {
    if (!security) {
        return false
    }
    if (security.tradeable !== undefined) {
        return security.tradeable
    }
    return isTradeableCashSecurityFallback(security)
}

/**
 * Whether Buy/Sell / bid-ask should open the order window.
 * Uses API `tradeable` when present (same as [isTradeableSecurity]).
 */
export function canOpenQuickTrade(
    security?: Pick<Security, 'tradeable' | 'instrument' | 'categoryCode' | 'derived'> | null,
): boolean {
    return isTradeableSecurity(security)
}

/** @deprecated Prefer [isTradeableSecurity] which reads API `tradeable`. */
export function isTradeableCashSecurity(
    security?: Pick<Security, 'tradeable' | 'instrument' | 'categoryCode'> | null,
): boolean {
    return isTradeableSecurity(security)
}

function isTradeableCashSecurityFallback(
    security: Pick<Security, 'instrument' | 'categoryCode'>,
): boolean {
    if (security.categoryCode === SecurityCategory_CategoryCode.STOCKS) {
        // Prefs / warrants / partly paid are not tradeable; equity is.
        return !security.instrument || security.instrument === Instrument[Instrument.EQUITY]
    }
    if (security.categoryCode === SecurityCategory_CategoryCode.FUNDS) {
        return isTradeableCashInstrument(security.instrument)
    }
    if (
        security.categoryCode === SecurityCategory_CategoryCode.FUTURES ||
        security.categoryCode === SecurityCategory_CategoryCode.OPTIONS
    ) {
        return true
    }
    return false
}

/** CNC / delivery-style cash order eligibility (excludes bonds and non-tradeable funds). */
export function isCncEligibleCashSecurity(
    security?: Pick<Security, 'tradeable' | 'instrument' | 'categoryCode'> | null,
): boolean {
    return isTradeableSecurity(security)
}

/** MTF is equity-only today. */
export function isMtfEligibleCashSecurity(security?: Pick<Security, 'categoryCode'> | null): boolean {
    return security?.categoryCode === SecurityCategory_CategoryCode.STOCKS
}

/** Chartable CM cash (includes non-tradeable bonds for market data). */
export function isChartableCmCashSecurity(security?: Pick<Security, 'categoryCode'> | null): boolean {
    return isCmCashCategory(security?.categoryCode)
}

export function securityCategoryDisplayLabel(
    categoryCode: SecurityCategory_CategoryCode,
    instrument?: string,
): string {
    return securityInstrumentDisplayLabel(instrument) ??
        (securityCategory_CategoryCodeToJSON(categoryCode) ?? '').toLowerCase()
}

/** Short label for search/meta rows — prefer instrument over category. */
export function securityInstrumentDisplayLabel(instrument?: string): string | undefined {
    if (!instrument) {
        return undefined
    }
    // Preferential shares: show "pref-shares" in search rows.
    if (instrument === Instrument[Instrument.PREFSHARES]) {
        return 'pref-shares'
    }
    const labeled = cashInstrumentDisplayLabel(instrument)
    if (labeled) {
        return labeled.toLowerCase()
    }
    switch (instrument) {
        case Instrument[Instrument.EQUITY]:
            return 'equity'
        case Instrument[Instrument.IDX]:
        case Instrument[Instrument.INX]:
            return 'index'
        case Instrument[Instrument.FUTSTK]:
            return 'stock future'
        case Instrument[Instrument.FUTIDX]:
            return 'index future'
        case Instrument[Instrument.FUTIVX]:
            return 'vix future'
        case Instrument[Instrument.OPTSTK]:
            return 'stock option'
        case Instrument[Instrument.OPTIDX]:
            return 'index option'
        case Instrument[Instrument.CUR]:
        case Instrument[Instrument.UNDCUR]:
        case Instrument[Instrument.UNDIRC]:
        case Instrument[Instrument.UNDIRD]:
        case Instrument[Instrument.UNDIRT]:
            return 'currency'
        case Instrument[Instrument.FUTCUR]:
        case Instrument[Instrument.FUTIRC]:
        case Instrument[Instrument.FUTIRT]:
            return 'currency future'
        case Instrument[Instrument.OPTCUR]:
        case Instrument[Instrument.OPTIRC]:
            return 'currency option'
        default:
            return instrument.toLowerCase().replace(/_/g, ' ')
    }
}

export function cashInstrumentDisplayLabel(instrument?: string): string | undefined {
    if (!instrument) {
        return undefined
    }
    switch (instrument) {
        case Instrument[Instrument.ETF]:
            return 'ETF'
        case Instrument[Instrument.REIT]:
            return 'REIT'
        case Instrument[Instrument.INVIT]:
            return 'InvIT'
        case Instrument[Instrument.AIF]:
            return 'AIF'
        case Instrument[Instrument.SGB]:
            return 'SGB'
        case Instrument[Instrument.MF_CLOSED]:
            return 'MF'
        case Instrument[Instrument.GOVT_SECURITY]:
            return 'G-Sec'
        case Instrument[Instrument.SDL]:
            return 'SDL'
        case Instrument[Instrument.TBILL]:
            return 'T-Bill'
        case Instrument[Instrument.CORP_BOND]:
            return 'Corp bond'
        case Instrument[Instrument.PREFSHARES]:
            return 'pref-shares'
        case Instrument[Instrument.WARRANT]:
            return 'Warrant'
        case Instrument[Instrument.PARTLY_PAID]:
            return 'Partly paid'
        default:
            return undefined
    }
}
